import { Span, InputSelect, InputText, InputSelectOption, Textarea, Grid } from "@goodfill/ui-components";
import { employmentStatuses, EmploymentStatus, CountryIsoCode } from "@goodfill/domain-customer-application-types";
import { CountryInput } from "../../../../../../countries/CountryInput";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { UserAuthState } from "../../../../../../state";
import { capitalizeEveryFirstWordLetter } from "@goodfill/string";
import { BusinessAndOccupationSelect } from "../../../BusinessAndOccupationSelect";
import {
  CustomerApplicationFormState,
  isEmployerCountryAbroadExplanationRequired,
  isEmploymentDetailRequired,
  CustomerApplicationFormStateEmployment,
} from "../../../formState/formState";
import { Controller, useFormContext } from "react-hook-form";

const employmentStatusesOptions: InputSelectOption[] = employmentStatuses.map((value) => ({
  value,
  label: capitalizeEveryFirstWordLetter(value.replace("-", " ")),
}));

interface Props {
  userStateData: UserAuthState;
  areAllDisabled: boolean;
}

export const Step4Employment = ({ userStateData, areAllDisabled }: Props) => {
  const { control, setValue, watch } = useFormContext<CustomerApplicationFormState>();

  const watchEmploymentStatus = watch("step4.employmentStatus");
  const watchEmployerCountry = watch("step4.employment.employer.country");

  const isEmploymentDetailShown =
    watchEmploymentStatus !== undefined && isEmploymentDetailRequired(watchEmploymentStatus);
  const employerCountryAbroadExplanationRequired = isEmployerCountryAbroadExplanationRequired(
    "USA", // intentionally hardcoded for now
    watchEmployerCountry as CountryIsoCode,
  );

  return (
    <div className="flex flex-col px-4 md:px-8 py-4 md:py-8 border-b border-neutral-110 items-center">
      <Span size="2xl" uppercase={false} font="semibold">
        Employment information
      </Span>

      <Span className="Steps_welcomeMessage" wrap={true}>
        To open a free account and start you investing safely, we need to know more about you. Let's get started!
      </Span>

      <Grid cols={2} gap={0.75} className="w-full">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label="Employment status"
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputSelect
                placeholder="Select type"
                options={employmentStatusesOptions}
                value={field.value ?? ""}
                onChange={(value) => {
                  field.onChange(value as EmploymentStatus);
                  setValue(
                    "step4.employment",
                    isEmploymentDetailRequired(value as EmploymentStatus)
                      ? ({ employer: { abroadExplanation: "" } } as CustomerApplicationFormStateEmployment)
                      : null,
                  );
                }}
                border={error === undefined ? "neutral" : "red"}
                onBlur={field.onBlur}
                ref={field.ref}
                id="employmentStatus"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step4.employmentStatus"
          control={control}
        />

        {isEmploymentDetailShown ? (
          <>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label="Employer Name"
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputText
                    placeholder="Enter name"
                    value={field.value ?? ""}
                    setValue={field.onChange}
                    border={error === undefined ? "neutral" : "red"}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id="employmentEmployerName"
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name="step4.employment.employer.name"
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label="Occupation"
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <BusinessAndOccupationSelect
                    placeholder="Select your occupation"
                    cognitoIdToken={userStateData.idToken}
                    selected={field.value}
                    onSelect={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    id="employmentOccupation"
                    disabled={areAllDisabled}
                  />
                </FieldGroup>
              )}
              name="step4.employment.occupation"
              control={control}
            />

            <Controller
              render={({ field, fieldState: { error } }) => (
                <FieldGroup
                  label="Employer Business Sector"
                  validation={error?.message ? [error.message] : []}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <InputText
                    value={field.value?.business ?? ""}
                    ref={field.ref}
                    onBlur={field.onBlur}
                    id="employmentOccupationBusiness"
                    disabled={true}
                    className="border border-color-gray"
                  />
                </FieldGroup>
              )}
              name="step4.employment.occupation"
              control={control}
            />

            {employerCountryAbroadExplanationRequired ? (
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <FieldGroup
                    label={"Employment Abroad Explanation"}
                    validation={error?.message ? [error.message] : []}
                    className="w-80-sm w-full-after-md"
                    fill
                  >
                    <Textarea
                      placeholder={"Enter explanation for employment abroad..."}
                      value={field.value}
                      setValue={field.onChange}
                      border={error === undefined ? "neutral" : "red"}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      id={"employmentEmployerAbroadExplanation"}
                      disabled={areAllDisabled}
                    />
                  </FieldGroup>
                )}
                name="step4.employment.employer.abroadExplanation"
                control={control}
              />
            ) : (
              <div className="flex flex-col my-2 md:my-4 gap-1 w-96" />
            )}

            <Controller
              render={({ field, fieldState: { error } }) => (
                <CountryInput
                  title={"Employer Country"}
                  onChange={(value) => {
                    field.onChange(value);
                    if (
                      !isEmployerCountryAbroadExplanationRequired(
                        "USA", // intentionally hardcoded for now
                        value as CountryIsoCode,
                      )
                    ) {
                      setValue("step4.employment.employer.abroadExplanation", "");
                    }
                  }}
                  validation={error}
                  selected={field.value}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"employmentEmployerCountry"}
                  disabled={areAllDisabled}
                />
              )}
              name="step4.employment.employer.country"
              control={control}
            />
          </>
        ) : null}
      </Grid>
    </div>
  );
};
